import { useState, useEffect, useCallback } from 'react';
import EvaluationLayout from '../../components/EvaluationLayout';
import {
  Webhook,
  Settings,
  Play,
  CheckCircle,
  XCircle,
  RefreshCw,
  Trash2,
  Globe,
  Save,
  X,
  BarChart3
} from 'lucide-react';
import Button from '../../components/ui/button';
import Input from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import Badge from '../../components/ui/badge';
import { toast } from 'react-hot-toast';
import webhookService from '../../services/webhookService';
import evaluationService from '../../services/evaluationService';

const WebhookInterface = () => {
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvaluation, setSelectedEvaluation] = useState(null);
  const [showWebhookModal, setShowWebhookModal] = useState(false);
  const [webhookForm, setWebhookForm] = useState({
    url: '',
    enabled: true,
    type: 'evaluation',
    events: ['evaluation.completed']
  });
  const [testingWebhook, setTestingWebhook] = useState(false);
  const [webhookTestResult, setWebhookTestResult] = useState(null);
  const [webhookDeliveries, setWebhookDeliveries] = useState([]);
  const [webhookStats, setWebhookStats] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadEvaluations(),
        loadWebhookDeliveries(),
        loadWebhookStatistics()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load webhook data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Refresh data when page becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [loadData]);

  const loadEvaluations = async () => {
    try {
      const response = await evaluationService.getEvaluations();
      console.log('Evaluation Service Response:', response);

      // Ensure evaluations is always an array
      let evaluationData = [];

      // Check different possible response structures
      if (response && response.data && Array.isArray(response.data.data)) {
        evaluationData = response.data.data;
      } else if (Array.isArray(response)) {
        evaluationData = response;
      } else if (response && Array.isArray(response.data)) {
        evaluationData = response.data;
      } else if (response && response.success && Array.isArray(response.success)) {
        evaluationData = response.success;
      } else if (response && response.evaluations && Array.isArray(response.evaluations)) {
        evaluationData = response.evaluations;
      }

      // Fallback to empty array if no valid data found
      if (!Array.isArray(evaluationData)) {
        console.error('Invalid evaluations data structure:', response);
        evaluationData = [];
        toast.error('Failed to load evaluations: Invalid data format');
      }

      // Add a fallback for webhook_url and webhook_enabled
      const processedEvaluations = evaluationData.map(evaluation => ({
        ...evaluation,
        webhook_url: evaluation.webhook_url || null,
        webhook_enabled: evaluation.webhook_enabled ?? true
      }));

      setEvaluations(processedEvaluations);
    } catch (error) {
      console.error('Error loading evaluations:', error);
      // Fallback to localStorage for backward compatibility
      try {
        const stored = localStorage.getItem('evaluations');
        const storedEvaluations = stored ? JSON.parse(stored) : [];

        if (Array.isArray(storedEvaluations)) {
          setEvaluations(storedEvaluations);
        } else {
          console.error('Invalid stored evaluations:', storedEvaluations);
          setEvaluations([]);
        }
      } catch (fallbackError) {
        console.error('Error loading from localStorage:', fallbackError);
        setEvaluations([]);
        toast.error('Failed to load evaluations');
      }
    }
  };

  const loadWebhookDeliveries = async () => {
    try {
      // Disable cache to always get fresh webhook endpoint data
      const response = await webhookService.getWebhookEndpoints({ per_page: 10 }, { cache: false });
      console.log('Webhook Endpoints Response:', response);

      if (response && response.success === true && response.data) {
        // Response has success: true and data property
        setWebhookDeliveries(Array.isArray(response.data) ? response.data : []);
      } else if (response && response.data && Array.isArray(response.data)) {
        // Response has data property with array
        setWebhookDeliveries(response.data);
      } else if (Array.isArray(response)) {
        // Response is array directly
        setWebhookDeliveries(response);
      } else {
        // Fallback to empty array
        console.warn('Unexpected webhook endpoints response format:', response);
        setWebhookDeliveries([]);
      }
    } catch (error) {
      console.error('Error loading webhook endpoints:', error);
      // Check for authentication error
      if (error.response && error.response.status === 401) {
        toast.error('Please log in to access webhook information');
        // Redirect to login page
        window.location.href = '/login';
      } else {
        console.error('Network error details:', error.message);
        setWebhookDeliveries([]);
      }
    }
  };

  const loadWebhookStatistics = async () => {
    try {
      // Disable cache to always get fresh statistics
      const response = await webhookService.getStatistics({}, { cache: false });
      console.log('Webhook Statistics Response:', response);

      if (response && response.success === true && response.data) {
        // Response has success: true and data property
        setWebhookStats(response.data);
      } else if (response && response.data) {
        // Response has data property directly
        setWebhookStats(response.data);
      } else if (response && typeof response === 'object') {
        // Response is the statistics object directly
        setWebhookStats(response);
      } else {
        // Fallback to empty stats
        console.warn('Unexpected statistics response format:', response);
        setWebhookStats({
          total_deliveries: 0,
          successful_deliveries: 0,
          failed_deliveries: 0,
          success_rate: 0
        });
      }
    } catch (error) {
      console.error('Error loading webhook statistics:', error);
      // Check for authentication error
      if (error.response && error.response.status === 401) {
        toast.error('Please log in to access webhook statistics');
        // Redirect to login page
        window.location.href = '/login';
      } else {
        console.error('Network error details:', error.message);
        setWebhookStats({
          total_deliveries: 0,
          successful_deliveries: 0,
          failed_deliveries: 0,
          success_rate: 0
        });
      }
    }
  };

  const handleSaveWebhook = async () => {
    if (!webhookForm.url) {
      toast.error('Please enter a webhook URL');
      return;
    }

    if (!webhookForm.events || webhookForm.events.length === 0) {
      toast.error('Please select at least one event');
      return;
    }

    try {
      let response;
      if (selectedEvaluation) {
        // Updating an existing evaluation's webhook
        const updateData = {
          webhook_url: webhookForm.url,
          webhook_enabled: webhookForm.enabled,
          webhook_events: webhookForm.events,
        };

        response = await evaluationService.updateEvaluation(selectedEvaluation.id, updateData);
      } else {
        // Creating a new standalone webhook endpoint
        response = await webhookService.createWebhookEndpoint({
          name: `${webhookForm.type === 'evaluation' ? 'Evaluation' : 'Run'} Webhook - ${new Date().toLocaleString()}`,
          url: webhookForm.url,
          method: 'POST',
          events: webhookForm.events,
          enabled: webhookForm.enabled,
          type: webhookForm.type
        });
      }

      if (response.success) {
        // Update local state
        if (selectedEvaluation) {
          const updatedEvaluations = evaluations.map(evaluation => {
            if (evaluation.id === selectedEvaluation.id) {
              return {
                ...evaluation,
                webhook_url: webhookForm.url,
                webhook_enabled: webhookForm.enabled,
                webhook_events: webhookForm.events,
                updated_at: new Date().toISOString()
              };
            }
            return evaluation;
          });

          setEvaluations(updatedEvaluations);
        } else {
          // Refresh webhook endpoints
          await loadWebhookDeliveries();
        }

        setShowWebhookModal(false);
        toast.success('Webhook configuration saved successfully');

        // Refresh statistics after update
        loadWebhookStatistics();
      } else {
        throw new Error(response.error);
      }
    } catch (error) {
      console.error('Error saving webhook:', error);
      toast.error('Failed to save webhook configuration');
    }
  };

  const handleTestWebhook = async () => {
    if (!webhookForm.url) {
      toast.error('Please enter a webhook URL');
      return;
    }

    setTestingWebhook(true);
    setWebhookTestResult(null);

    try {
      const response = await webhookService.testWebhook(webhookForm.url);

      setWebhookTestResult({
        success: response.success,
        status_code: response.status_code || (response.success ? 200 : 400),
        message: response.message || (response.success ? 'Webhook test successful' : 'Webhook test failed'),
        response_time: response.response_time
      });

      if (response.success) {
        toast.success('Webhook test successful');
      } else {
        toast.error(`Webhook test failed: ${response.message || response.error}`);
      }
    } catch (error) {
      console.error('Error testing webhook:', error);
      setWebhookTestResult({
        success: false,
        status_code: 500,
        message: 'Failed to connect to webhook URL'
      });
      toast.error('Failed to test webhook');
    } finally {
      setTestingWebhook(false);
    }
  };

  const handleDeleteWebhook = async (webhookId) => {
    if (!window.confirm('Are you sure you want to delete this webhook endpoint?')) {
      return;
    }

    try {
      const response = await webhookService.deleteWebhookEndpoint(webhookId);

      if (response.success) {
        // Remove the deleted webhook from the list
        const updatedWebhooks = webhookDeliveries.filter(webhook => webhook.id !== webhookId);
        setWebhookDeliveries(updatedWebhooks);

        toast.success('Webhook endpoint deleted successfully');

        // Refresh webhook statistics
        loadWebhookStatistics();
      } else {
        throw new Error(response.error || 'Failed to delete webhook endpoint');
      }
    } catch (error) {
      console.error('Error deleting webhook:', error);
      toast.error('Failed to delete webhook endpoint');
    }
  };

  if (loading) {
    return (
      <EvaluationLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </EvaluationLayout>
    );
  }

  return (
    <EvaluationLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-foreground">Webhook Management</h1>
            <p className="text-muted-foreground">
              Configure and monitor webhook notifications for your evaluations
            </p>
          </div>
          <Button
            onClick={() => {
              setSelectedEvaluation(null);
              setWebhookForm({
                url: '',
                enabled: true,
                type: 'evaluation',
                events: ['evaluation.completed']
              });
              setWebhookTestResult(null);
              setShowWebhookModal(true);
            }}
            variant="primary"
            className="whitespace-nowrap flex-shrink-0"
          >
            <Webhook className="w-4 h-4 mr-2" />
            New Webhook
          </Button>
        </div>

        {/* Statistics Cards */}
        {webhookStats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center space-x-2">
                  <BarChart3 className="w-4 h-4 text-blue-500" />
                  <div>
                    <p className="text-sm text-muted-foreground">Total Deliveries</p>
                    <p className="text-2xl font-bold">{webhookStats.total_deliveries}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <div>
                    <p className="text-sm text-muted-foreground">Successful</p>
                    <p className="text-2xl font-bold">{webhookStats.successful_deliveries}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center space-x-2">
                  <XCircle className="w-4 h-4 text-red-500" />
                  <div>
                    <p className="text-sm text-muted-foreground">Failed</p>
                    <p className="text-2xl font-bold">{webhookStats.failed_deliveries}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center space-x-2">
                  <BarChart3 className="w-4 h-4 text-purple-500" />
                  <div>
                    <p className="text-sm text-muted-foreground">Success Rate</p>
                    <p className="text-2xl font-bold">{webhookStats.success_rate}%</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Evaluations with Webhook Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Webhook className="w-5 h-5" />
              Webhook Configuration
            </CardTitle>
          </CardHeader>
          <CardContent>
            {webhookDeliveries.length === 0 ? (
              <div className="text-center py-8">
                <Webhook className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">No Webhook Endpoints Found</h3>
                <p className="text-muted-foreground mb-4">
                  Webhook endpoints will appear here when available
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {webhookDeliveries.map((webhook) => (
                  <div
                    key={webhook.id}
                    className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-accent/50 transition-colors"
                  >
                    <div className="flex-1">
                      <h3 className="font-medium text-foreground">{webhook.name || 'Unnamed Webhook'}</h3>
                      <div className="flex items-center gap-2 mt-2">
                        <Globe className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">{webhook.url}</span>
                        {webhook.enabled ? (
                          <Badge variant="success">Enabled</Badge>
                        ) : (
                          <Badge variant="secondary">Disabled</Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedEvaluation(null);
                          setWebhookForm({
                            url: webhook.url,
                            enabled: webhook.enabled,
                            events: webhook.events || ['evaluation.completed']
                          });
                          setShowWebhookModal(true);
                        }}
                      >
                        <Settings className="w-4 h-4 mr-2" />
                        Configure
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteWebhook(webhook.id)}
                        className="text-red-500 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Webhook Configuration Modal */}
        {showWebhookModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-4 border-b border-border sticky top-0 bg-card">
                <h3 className="text-lg font-semibold">Configure Webhook</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowWebhookModal(false)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Webhook Type
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                    value={webhookForm.type || 'evaluation'}
                    onChange={(e) => setWebhookForm({ ...webhookForm, type: e.target.value })}
                  >
                    <option value="evaluation">Evaluation Scheduling</option>
                    <option value="run">Evaluation Results</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Webhook URL
                  </label>
                  <Input
                    type="url"
                    placeholder="https://your-webhook-endpoint.com/webhook"
                    value={webhookForm.url}
                    onChange={(e) => setWebhookForm({ ...webhookForm, url: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Events
                  </label>
                  <div className="space-y-2">
                    {webhookForm.type === 'evaluation' ? (
                      <>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={webhookForm.events?.includes('evaluation.completed') || false}
                            onChange={(e) => {
                              const events = webhookForm.events || [];
                              if (e.target.checked) {
                                setWebhookForm({ ...webhookForm, events: [...events, 'evaluation.completed'] });
                              } else {
                                setWebhookForm({ ...webhookForm, events: events.filter(ev => ev !== 'evaluation.completed') });
                              }
                            }}
                            className="rounded border-border"
                          />
                          <span className="text-sm text-foreground">Evaluation Completed</span>
                        </label>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={webhookForm.events?.includes('evaluation.scheduled') || false}
                            onChange={(e) => {
                              const events = webhookForm.events || [];
                              if (e.target.checked) {
                                setWebhookForm({ ...webhookForm, events: [...events, 'evaluation.scheduled'] });
                              } else {
                                setWebhookForm({ ...webhookForm, events: events.filter(ev => ev !== 'evaluation.scheduled') });
                              }
                            }}
                            className="rounded border-border"
                          />
                          <span className="text-sm text-foreground">Evaluation Scheduled</span>
                        </label>
                      </>
                    ) : (
                      <>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={webhookForm.events?.includes('run.completed') || false}
                            onChange={(e) => {
                              const events = webhookForm.events || [];
                              if (e.target.checked) {
                                setWebhookForm({ ...webhookForm, events: [...events, 'run.completed'] });
                              } else {
                                setWebhookForm({ ...webhookForm, events: events.filter(ev => ev !== 'run.completed') });
                              }
                            }}
                            className="rounded border-border"
                          />
                          <span className="text-sm text-foreground">Run Completed</span>
                        </label>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={webhookForm.events?.includes('run.failed') || false}
                            onChange={(e) => {
                              const events = webhookForm.events || [];
                              if (e.target.checked) {
                                setWebhookForm({ ...webhookForm, events: [...events, 'run.failed'] });
                              } else {
                                setWebhookForm({ ...webhookForm, events: events.filter(ev => ev !== 'run.failed') });
                              }
                            }}
                            className="rounded border-border"
                          />
                          <span className="text-sm text-foreground">Run Failed</span>
                        </label>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="webhook-enabled"
                    checked={webhookForm.enabled}
                    onChange={(e) => setWebhookForm({ ...webhookForm, enabled: e.target.checked })}
                    className="rounded border-border"
                  />
                  <label htmlFor="webhook-enabled" className="text-sm text-foreground">
                    Enable webhook notifications
                  </label>
                </div>

                {webhookTestResult && (
                  <div className={`p-3 rounded-md ${webhookTestResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                    }`}>
                    <div className="flex items-center gap-2">
                      {webhookTestResult.success ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-600" />
                      )}
                      <span className={`text-sm ${webhookTestResult.success ? 'text-green-800' : 'text-red-800'
                        }`}>
                        {webhookTestResult.message}
                      </span>
                    </div>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={handleTestWebhook}
                    disabled={testingWebhook || !webhookForm.url}
                    className="flex-1"
                  >
                    {testingWebhook ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Play className="w-4 h-4 mr-2" />
                    )}
                    Test Webhook
                  </Button>
                  <Button onClick={handleSaveWebhook} className="flex-1">
                    <Save className="w-4 h-4 mr-2" />
                    Save
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </EvaluationLayout>
  );
};

export default WebhookInterface;
